import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Dialog, Textarea } from '@artosera/ui';
import { formaterDate } from '../../../../lib/format.js';
import type { RecetteOuverture } from '../../../../types.js';
import formStyles from '../../../../styles/form.module.css';
import pageStyles from '../../../../styles/page.module.css';
import styles from './Recette.module.css';

const MOTIF_MIN = 8;

/**
 * Ce que dit la galerie : sa validation (A3) avec le procès-verbal, ses
 * signalements du plus récent au plus ancien, et, tant qu'elle n'a pas
 * validé, la dérogation que la direction de Venio peut poser (motif
 * obligatoire, second facteur récent).
 */
export function ValidationGalerie({
  recette,
  peutDeroger,
  enCours,
  surDerogation,
  boiteMasquee,
}: {
  recette: RecetteOuverture;
  peutDeroger: boolean;
  enCours: boolean;
  /** Rend `true` si la dérogation est posée ; la boîte se referme alors. */
  surDerogation: (motif: string) => Promise<boolean>;
  /** Une autre boîte (le second facteur) a la main : celle-ci s'efface. */
  boiteMasquee: boolean;
}): JSX.Element {
  const { t } = useTranslation('console');
  const cle = 'ouvertures.fiche.recette';
  const { validation, derogation } = recette;
  const [boite, setBoite] = useState(false);
  const [motif, setMotif] = useState('');
  const [motifErreur, setMotifErreur] = useState(false);

  const signalements = [...recette.signalements].sort(
    (a, b) => new Date(b.le).getTime() - new Date(a.le).getTime(),
  );
  const peutProposer =
    validation === null && derogation === null && recette.etat === 'recette' && peutDeroger;

  const confirmer = async (): Promise<void> => {
    if (motif.trim().length < MOTIF_MIN) {
      setMotifErreur(true);
      return;
    }
    if (await surDerogation(motif.trim())) {
      setBoite(false);
      setMotif('');
    }
  };

  return (
    <section className={pageStyles.panneau} aria-labelledby="rec-validation">
      <div className={pageStyles.sectionTitre}>
        <h2 id="rec-validation">{t(`${cle}.validation.titre`)}</h2>
        <Badge tone={validation ? 'available' : 'reserved'}>
          {t(`${cle}.validation.${validation ? 'validee' : 'enAttente'}`)}
        </Badge>
      </div>

      {validation ? (
        <dl className={styles.faits}>
          <dt>{t(`${cle}.validation.validee`)}</dt>
          <dd>
            {t(`${cle}.validation.parLe`, {
              par: validation.par,
              date: formaterDate(validation.le, '—'),
            })}
            <span className={styles.etapeNote}>
              {t(`${cle}.validation.controlesFiges`, { count: validation.controles.length })}
            </span>
          </dd>
          <dt>{t(`${cle}.validation.pv`)}</dt>
          <dd>
            {validation.pv.disponible
              ? t(
                  validation.pv.rangeAuCoffre
                    ? `${cle}.validation.pvCoffre`
                    : `${cle}.validation.pvDocuments`,
                  { date: formaterDate(validation.pv.le, '—') },
                )
              : t(`${cle}.validation.pvAttente`)}
          </dd>
        </dl>
      ) : (
        <p className={styles.texte}>{t(`${cle}.validation.texteAttente`)}</p>
      )}

      <h3 className={styles.titreBloc}>
        {t(`${cle}.validation.signalements`)}
        {signalements.length > 0 ? ` (${String(signalements.length)})` : ''}
      </h3>
      {signalements.length === 0 ? (
        <p className={styles.discret}>{t(`${cle}.validation.aucunSignalement`)}</p>
      ) : (
        <ul className={styles.signalements}>
          {signalements.map((signalement) => (
            <li key={`${signalement.le}-${signalement.par}`} className={styles.signalement}>
              <p>{signalement.texte}</p>
              <span className={styles.discret}>
                {t(`${cle}.validation.signalePar`, {
                  par: signalement.par,
                  date: formaterDate(signalement.le, '—'),
                })}
              </span>
            </li>
          ))}
        </ul>
      )}

      {validation === null && (derogation !== null || peutProposer) ? (
        <>
          <h3 className={styles.titreBloc}>{t(`${cle}.derogation.titre`)}</h3>
          {derogation ? (
            <dl className={styles.faits}>
              <dt>{t(`${cle}.derogation.motifPose`)}</dt>
              <dd>
                {derogation.motif}
                <span className={styles.etapeNote}>
                  {t(`${cle}.derogation.posee`, {
                    par: derogation.par,
                    date: formaterDate(derogation.le, '—'),
                  })}
                </span>
              </dd>
            </dl>
          ) : (
            <>
              <p className={styles.texte}>{t(`${cle}.derogation.texte`)}</p>
              <Button variant="secondary" disabled={enCours} onClick={() => setBoite(true)}>
                {t(`${cle}.derogation.action`)}
              </Button>
            </>
          )}
        </>
      ) : null}

      <Dialog
        open={boite && !boiteMasquee}
        onOpenChange={(ouvert) => {
          setBoite(ouvert);
          if (!ouvert) setMotifErreur(false);
        }}
        title={t(`${cle}.derogation.dialogue.titre`)}
        description={t(`${cle}.derogation.dialogue.texte`)}
        closeLabel={t(`${cle}.derogation.dialogue.fermer`)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setBoite(false)}>
              {t(`${cle}.derogation.dialogue.annuler`)}
            </Button>
            <Button variant="danger" loading={enCours} onClick={() => void confirmer()}>
              {t(`${cle}.derogation.dialogue.confirmer`)}
            </Button>
          </>
        }
      >
        <div className={formStyles.champ}>
          <label htmlFor="rec-derogation-motif">{t(`${cle}.derogation.dialogue.motif`)}</label>
          <Textarea
            id="rec-derogation-motif"
            rows={3}
            maxLength={1000}
            value={motif}
            aria-invalid={motifErreur}
            onChange={(evenement) => {
              setMotif(evenement.target.value);
              setMotifErreur(false);
            }}
          />
          {motifErreur ? (
            <p className={formStyles.erreur} role="alert">
              {t(`${cle}.derogation.dialogue.erreur`)}
            </p>
          ) : (
            <p className={formStyles.aide}>{t(`${cle}.derogation.dialogue.aide`)}</p>
          )}
        </div>
      </Dialog>
    </section>
  );
}
